import {useTranslation} from 'react-i18next'
import {useEffect, useState} from 'react'
import {useAppDispatch} from '../../../../redux/hooks'
import {MemStats} from '../../../../redux/Slice/adminSlice'
import {ApexOptions} from 'apexcharts'
import ReactApexChart from 'react-apexcharts'
import Loader from 'app/custom/loader/Loader'

const CHART_COLORS_50 = [
  '#3E97FF',
  '#F1416C',
  '#50CD89',
  '#FFC700',
  '#7239EA',
  '#00A3FF',
  '#F1BC00',
  '#E4405F',
  '#17C653',
  '#8950FC',
  '#FF6B35',
  '#1BC5BD',
  '#B23CFD',
  '#FD7E14',
  '#0DCAF0',
  '#D63384',
  '#20C997',
  '#6610F2',
  '#FFB020',
  '#2E7D32',
  '#C2185B',
  '#0288D1',
  '#F4511E',
  '#5E35B1',
  '#00897B',
  '#43A047',
  '#FB8C00',
  '#3949AB',
  '#E53935',
  '#00ACC1',
  '#7CB342',
  '#8E24AA',
  '#FDD835',
  '#546E7A',
  '#D81B60',
  '#1E88E5',
  '#6D4C41',
  '#039BE5',
  '#F06292',
  '#AED581',
  '#BA68C8',
  '#4DB6AC',
  '#FF8A65',
  '#9575CD',
  '#4FC3F7',
  '#DCE775',
  '#A1887F',
  '#90A4AE',
  '#F48FB1',
  '#80CBC4',
]

const toPercent = (counts: number[]): number[] => {
  const total = counts.reduce((sum: number, value: number) => sum + value, 0)
  if (!total) return counts.map(() => 0)
  const exact = counts.map((value: number) => (value / total) * 100)
  const result = exact.map((value: number) => Math.floor(value))
  let remaining = 100 - result.reduce((sum: number, value: number) => sum + value, 0)
  const byFraction = exact
    .map((value: number, index: number) => ({index, fraction: value - Math.floor(value)}))
    .sort((a, b) => b.fraction - a.fraction)
  for (let i = 0; i < byFraction.length && remaining > 0; i++) {
    result[byFraction[i].index] += 1
    remaining--
  }
  return result
}

const MemberStatistic = () => {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const [statistics, setStatistics] = useState<any>([])
  const [loader, setLoader] = useState<any>(true)

  useEffect(() => {
    dispatch(MemStats()).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setStatistics(res.payload)
        setLoader(false)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const prefixColors =
    statistics?.prefix?.map((item: any) =>
      item.name === 'LABEL.FEMALE' ? '#FF9BBD' : '#6EB5FF'
    ) || []

  // Municipality — bar chart. Counts are kept for the tooltip, percentages are plotted.
  const municipalityCounts = statistics?.municipality?.map((item: any) => Number(item.total)) || []
  const municipalityLabels =
    statistics?.municipality?.map((item: any) =>
      item.name === 'LABEL.OTHER' ? t(item.name) : item.name || '-'
    ) || []
  const municipalityColors = municipalityLabels.map(
    (_: string, index: number) => CHART_COLORS_50[index % CHART_COLORS_50.length]
  )
  const municipalitySeries = toPercent(municipalityCounts)

  // Pie charts — the series stays as counts so the slice angles are exact,
  // and the labels use the pre-computed percentages that total 100.
  const prefixSeries = statistics?.prefix?.map((item: any) => Number(item.total)) || []
  const prefixPercents = toPercent(prefixSeries)
  const prefixLabels = statistics?.prefix?.map((item: any) => t(item.name) || '-') || []

  const categorySeries = statistics?.category?.map((item: any) => Number(item.total)) || []
  const categoryPercents = toPercent(categorySeries)
  const categoryLabels = statistics?.category?.map((item: any) => item.name || '-') || []

  const statusSeries = statistics?.status?.map((item: any) => Number(item.total)) || []
  const statusPercents = toPercent(statusSeries)
  const statusLabels = statistics?.status?.map((item: any) => t('LABEL.' + item.name) || '-') || []

  const chartTitle = (text: string) => ({
    text,
    align: 'left' as const,
    margin: 10,
    offsetX: 10,
    offsetY: 10,
    style: {
      fontSize: '16px',
      fontWeight: 700,
      color: '#181C32',
    },
  })

  const createPieChartOptions = (
    title: string,
    labels: string[],
    percents: number[],
    colors?: string[]
  ): ApexOptions => ({
    chart: {
      type: 'pie',
      height: 350,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
    },

    title: chartTitle(title),

    labels: labels,

    ...(colors ? {colors} : {}),

    legend: {
      position: 'bottom',
      labels: {
        colors: '#333',
        useSeriesColors: false,
      },
    },

    // whole-number percentages, adding up to exactly 100
    dataLabels: {
      enabled: true,
      formatter: function (_val: number, opts: any) {
        return `${percents[opts?.seriesIndex] ?? 0}%`
      },
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
      },
    },

    // hover shows the real count
    tooltip: {
      y: {
        formatter: function (val: number, opts: any) {
          const percent = percents[opts?.seriesIndex] ?? 0
          return `${val} (${percent}%)`
        },
      },
    },

    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: '100%',
          },
          legend: {
            position: 'bottom',
          },
        },
      },
    ],
  })

  const createBarChartOptions = (
    title: string,
    labels: string[],
    colors: string[],
    counts: number[]
  ): ApexOptions => ({
    chart: {
      type: 'bar',
      height: 420,
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
    },

    title: chartTitle(title),

    colors: colors,

    plotOptions: {
      bar: {
        distributed: true, // one color per bar
        borderRadius: 4,
        columnWidth: '55%',
        dataLabels: {
          position: 'top',
        },
      },
    },

    dataLabels: {
      enabled: true,
      offsetY: -20,
      formatter: function (val: number) {
        return `${val}%`
      },
      style: {
        fontSize: '11px',
        fontWeight: 'bold',
        colors: ['#333'],
      },
    },

    legend: {
      show: false, // labels already appear on the x-axis
    },

    xaxis: {
      categories: labels,
      labels: {
        rotate: -45,
        rotateAlways: labels.length > 8,
        hideOverlappingLabels: false,
        trim: true,
        style: {
          fontSize: '11px',
          colors: '#333',
        },
      },
      tickPlacement: 'on',
    },

    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: {
        formatter: function (val: number) {
          return `${Math.round(val)}%`
        },
        style: {
          colors: '#333',
        },
      },
    },

    grid: {
      borderColor: '#EFF2F5',
      strokeDashArray: 4,
    },

    // hover shows the real count next to the percentage
    tooltip: {
      y: {
        formatter: function (val: number, opts: any) {
          const count = counts[opts?.dataPointIndex] ?? 0
          return `${count} (${val}%)`
        },
      },
    },

    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: '100%',
            height: 380,
          },
          plotOptions: {
            bar: {
              columnWidth: '70%',
            },
          },
          dataLabels: {
            enabled: false,
          },
        },
      },
    ],
  })

  const municipalityOptions = createBarChartOptions(
    t('LABEL.MEMBERS_BY_COMMUNE'),
    municipalityLabels,
    municipalityColors,
    municipalityCounts
  )
  const prefixOptions = createPieChartOptions(
    t('LABEL.GENDER'),
    prefixLabels,
    prefixPercents,
    prefixColors
  )
  const categoryOptions = createPieChartOptions(
    t('LABEL.MEMBERSHIP'),
    categoryLabels,
    categoryPercents
  )
  const statusOptions = createPieChartOptions(t('LABEL.STATUS'), statusLabels, statusPercents)

  if (loader) return <Loader />

  return (
    <>
      <div className='row g-5 mb-5'>
        <div className='col-12'>
          <div className='card shadow-sm h-100'>
            <div className='card-body'>
              <ReactApexChart
                options={municipalityOptions}
                series={[{name: t('LABEL.MUNICIPALITY'), data: municipalitySeries}]}
                type='bar'
                height={420}
                width='100%'
              />
            </div>
          </div>
        </div>
      </div>
      <div className='row g-5 mb-5'>
        <div className='col-md-4'>
          <div className='card shadow-sm h-100'>
            <div className='card-body'>
              <ReactApexChart
                options={prefixOptions}
                series={prefixSeries}
                type='pie'
                height={350}
                width='100%'
              />
            </div>
          </div>
        </div>

        <div className='col-md-4'>
          <div className='card shadow-sm h-100'>
            <div className='card-body'>
              <ReactApexChart
                options={categoryOptions}
                series={categorySeries}
                type='pie'
                height={350}
                width='100%'
              />
            </div>
          </div>
        </div>

        <div className='col-md-4'>
          <div className='card shadow-sm h-100'>
            <div className='card-body'>
              <ReactApexChart
                options={statusOptions}
                series={statusSeries}
                type='pie'
                height={350}
                width='100%'
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default MemberStatistic
